import React from 'react';
import { Skeleton } from '@planne-software/uni';
import { formatCents } from '@src/common';
import { useTranslate } from '@src/core/hooks';
import ReservationTotal from '@src/domains/products/features/product-booking/components/ReservationTotal';
import type { UnitPrice } from '@src/domains/products/features/product-booking/core/computeUnitPrice';
import type { Installment } from '@src/domains/products/features/product-booking/core/computeInstallment';
import { getDisplayCents } from '@src/domains/products/features/product-booking/core/getDisplayCents';
import { computeDiscountPercent } from '@src/domains/products/features/product-booking/utils/computeDiscountPercent';
import {
  Wrapper,
  AboveLabel,
  PriceLine,
  HeroPrice,
  PriceSuffix,
  SecondaryLine,
  OldPrice,
  OldPriceLine,
  DiscountRow,
  DiscountBadge,
} from './styles';

type Props = {
  unitPrice: UnitPrice | null;
  installment: Installment | null;
  totalCents: number | null;
  loading?: boolean;
};

// The installments branch below is only reachable once the payment-preview
// endpoint exists (see ../../core/computeInstallment.ts). Until then, callers
// pass installment={null} and only the À vista branch renders.
const PriceContainer: React.FC<Props> = ({ unitPrice, installment, totalCents, loading = false }) => {
  const { i18n } = useTranslate();

  if (totalCents !== null) return <ReservationTotal totalCents={totalCents} />;

  if (loading) {
    return (
      <Wrapper data-testid='price-container-skeleton'>
        <AboveLabel>
          <Skeleton width='60px' height='16px' sx={{ mb: '8px' }} />
        </AboveLabel>
        <PriceLine>
          <HeroPrice>
            <Skeleton width='120px' height='32px' sx={{ mr: '4px' }} />
          </HeroPrice>
          <PriceSuffix>
            <Skeleton width='40px' height='20px' />
          </PriceSuffix>
        </PriceLine>
      </Wrapper>
    );
  }

  if (!unitPrice) return null;

  const hasInstallments = installment != null && installment.count > 1;
  const fullPriceCents = getDisplayCents(unitPrice);
  const oldPriceCents = unitPrice.kind === 'single' ? unitPrice.oldCents : null;
  const discountPercent = computeDiscountPercent(fullPriceCents, oldPriceCents);
  const hasOldPrice = oldPriceCents != null && oldPriceCents > 0;
  const isStartingFrom = unitPrice.kind === 'range' || !hasOldPrice;

  if (hasInstallments) {
    return (
      <Wrapper>
        <AboveLabel>
          {i18n('product_booking_price_in_up_to_installments', { count: String(installment.count) })}
        </AboveLabel>
        <PriceLine>
          <HeroPrice>{formatCents(installment.perInstallmentCents)}</HeroPrice>
          {installment.isInterestFree && <PriceSuffix>{i18n('product_booking_price_interest_free')}</PriceSuffix>}
        </PriceLine>
        {discountPercent ? (
          <DiscountRow>
            <OldPrice>{formatCents(oldPriceCents!)}</OldPrice>
            <SecondaryLine>
              {i18n('product_booking_price_at_sight_compact', { price: formatCents(fullPriceCents) })}
            </SecondaryLine>
            <DiscountBadge>-{discountPercent}%</DiscountBadge>
          </DiscountRow>
        ) : (
          <SecondaryLine>
            {i18n('product_booking_price_or_at_sight', { price: formatCents(fullPriceCents) })}
          </SecondaryLine>
        )}
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {isStartingFrom && <AboveLabel>{i18n('product_booking_price_starting_from')}</AboveLabel>}
      {hasOldPrice && (
        <OldPriceLine>
          <OldPrice>{formatCents(oldPriceCents)}</OldPrice>
          {discountPercent && <DiscountBadge>-{discountPercent}%</DiscountBadge>}
        </OldPriceLine>
      )}
      <PriceLine>
        <HeroPrice>{formatCents(fullPriceCents)}</HeroPrice>
        <PriceSuffix>{i18n('product_booking_price_at_sight_label')}</PriceSuffix>
      </PriceLine>
    </Wrapper>
  );
};

export default PriceContainer;
